import {
  buildCashbookMovements,
  buildDebtMovements,
  collectDebtOwners,
  isCashbookOpeningConfigured,
  localTodayIso,
  migrateOpeningDebtLinesIfNeeded,
  type CashbookDebtEntryRecord,
  type CashbookEntryRecord,
  type CashbookOpeningDebtLine,
  type CashbookSettings,
  type ImportDuplicateKey
} from '@finvox/shared/cashbook';
import { roundMoney } from '@finvox/shared/money';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
  type DocumentData,
  type Firestore,
  type QueryConstraint,
  type Transaction
} from 'firebase/firestore';
import { isFirestoreIndexError } from './errors';

export type CashbookContext = {
  settings: CashbookSettings;
  entries: CashbookEntryRecord[];
  debtEntries: CashbookDebtEntryRecord[];
  movements: ReturnType<typeof buildCashbookMovements>;
  debtMovements: ReturnType<typeof buildDebtMovements>;
  hasActiveEntries: boolean;
};

const CASHBOOK_CONTEXT_TTL_MS = 20_000;
const cashbookContextCache = new Map<
  string,
  { expiresAt: number; epoch: number; value: CashbookContext }
>();
/** Bumped on invalidate so in-flight fetches cannot re-poison the cache with stale data. */
const cashbookContextEpochByOrg = new Map<string, number>();

function cashbookContextEpoch(orgId: string): number {
  return cashbookContextEpochByOrg.get(orgId) ?? 0;
}

/** @internal test helper — true only if no invalidate happened since fetch started. */
export function shouldAcceptCashbookCacheWrite(orgId: string, epochAtStart: number): boolean {
  return epochAtStart === cashbookContextEpoch(orgId);
}

export function invalidateCashbookContextCache(orgId: string): void {
  cashbookContextEpochByOrg.set(orgId, cashbookContextEpoch(orgId) + 1);
  for (const key of cashbookContextCache.keys()) {
    if (key.startsWith(`${orgId}:`)) cashbookContextCache.delete(key);
  }
}

/** @internal test helper */
export function clearCashbookContextCacheForTests(): void {
  cashbookContextCache.clear();
  cashbookContextEpochByOrg.clear();
}

export function cashbookSettingsRef(db: Firestore, orgId: string) {
  return doc(db, 'organizations', orgId, 'settings', 'cashbook');
}

export function cashbookEntriesCol(db: Firestore, orgId: string) {
  return collection(db, 'organizations', orgId, 'cashbookEntries');
}

export function cashbookDebtEntriesCol(db: Firestore, orgId: string) {
  return collection(db, 'organizations', orgId, 'cashbookDebtEntries');
}

export function bumpSyncVersion(tx: Transaction, db: Firestore, orgId: string): void {
  tx.set(cashbookSettingsRef(db, orgId), { syncVersion: serverTimestamp() }, { merge: true });
}

export function syncVersionString(settings: CashbookSettings): string | undefined {
  const v = settings.syncVersion;
  if (!v) return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toISOString();
  }
  return String(v);
}

export function mapCashbookEntry(id: string, data: DocumentData): CashbookEntryRecord {
  const fundingRaw = data.fundingSource;
  const fundingSource =
    fundingRaw === 'drawer' || fundingRaw === 'treasury' || fundingRaw === 'unclassified'
      ? fundingRaw
      : data.type === 'out'
        ? 'unclassified'
        : undefined;
  const salesOriginRaw = data.salesOrigin;
  const salesOrigin =
    salesOriginRaw === 'drawer' || salesOriginRaw === 'payvox' || salesOriginRaw === 'manual'
      ? salesOriginRaw
      : data.source === 'payvox'
        ? 'payvox'
        : undefined;

  return {
    id,
    type: data.type === 'in' ? 'in' : 'out',
    amount: roundMoney(String(data.amount ?? '0')),
    date: String(data.date ?? ''),
    categoryKey: String(data.categoryKey ?? ''),
    memo: data.memo ? String(data.memo) : undefined,
    status: data.status === 'voided' ? 'voided' : 'active',
    ...(fundingSource ? { fundingSource } : {}),
    ...(data.drawerCount != null && data.drawerCount !== ''
      ? { drawerCount: roundMoney(String(data.drawerCount)) }
      : {}),
    ...(data.salesDayKey ? { salesDayKey: String(data.salesDayKey) } : {}),
    ...(salesOrigin ? { salesOrigin } : {}),
    ...(data.source ? { source: String(data.source) } : {}),
    ...(data.payvoxDayKey ? { payvoxDayKey: String(data.payvoxDayKey) } : {}),
    ...(data.payvoxSaleId ? { payvoxSaleId: String(data.payvoxSaleId) } : {}),
    ...(data.imported === true ? { imported: true } : {})
  };
}

export function mapDebtEntry(id: string, data: DocumentData): CashbookDebtEntryRecord {
  const allocationsRaw = data.allocations;
  const allocations =
    Array.isArray(allocationsRaw) && allocationsRaw.length > 0
      ? allocationsRaw
          .map((item) => {
            if (!item || typeof item !== 'object') return null;
            const row = item as Record<string, unknown>;
            const debtEntryId = String(row.debtEntryId ?? '').trim();
            if (!debtEntryId) return null;
            return {
              debtEntryId,
              amount: roundMoney(String(row.amount ?? '0'))
            };
          })
          .filter((a): a is { debtEntryId: string; amount: string } => a != null)
      : undefined;

  return {
    id,
    type: data.type === 'payment' ? 'payment' : 'incurred',
    amount: roundMoney(String(data.amount ?? '0')),
    date: String(data.date ?? ''),
    categoryKey: String(data.categoryKey ?? ''),
    creditor: data.creditor ? String(data.creditor) : undefined,
    memo: data.memo ? String(data.memo) : undefined,
    status: data.status === 'voided' ? 'voided' : 'active',
    linkedCashbookEntryId: data.linkedCashbookEntryId
      ? String(data.linkedCashbookEntryId)
      : undefined,
    appliedToDebtEntryId: data.appliedToDebtEntryId
      ? String(data.appliedToDebtEntryId)
      : undefined,
    ...(allocations && allocations.length > 0 ? { allocations } : {})
  };
}

function mapOpeningDebtLines(raw: unknown): CashbookOpeningDebtLine[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const lines: CashbookOpeningDebtLine[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const id = String(row.id ?? '').trim();
    if (!id) continue;
    lines.push({
      id,
      creditor: String(row.creditor ?? '').trim(),
      amount: roundMoney(String(row.amount ?? '0')),
      categoryKey: String(row.categoryKey ?? 'other'),
      ...(row.memo ? { memo: String(row.memo) } : {})
    });
  }
  return lines;
}

export async function loadCashbookSettings(db: Firestore, orgId: string): Promise<CashbookSettings> {
  const snap = await getDoc(cashbookSettingsRef(db, orgId));
  const data = snap.data() ?? {};
  const settings: CashbookSettings = {
    ...(data as CashbookSettings),
    openingDebtLines: mapOpeningDebtLines(data.openingDebtLines)
  };
  return settings;
}

/** Persist one-time openingDebtLines migration when needed. Returns settings to use. */
export async function ensureOpeningDebtLinesMigrated(
  db: Firestore,
  orgId: string,
  settings: CashbookSettings
): Promise<CashbookSettings> {
  const migrated = migrateOpeningDebtLinesIfNeeded(settings);
  if (!migrated) return settings;
  await setDoc(
    cashbookSettingsRef(db, orgId),
    {
      openingDebtLines: migrated.openingDebtLines ?? [],
      openingDebtAmount: migrated.openingDebtAmount ?? '0.00',
      syncVersion: serverTimestamp()
    },
    { merge: true }
  );
  invalidateCashbookContextCache(orgId);
  return migrated;
}

export async function assertPeriodOpen(db: Firestore, orgId: string, periodId: string): Promise<void> {
  const period = await getDoc(doc(db, 'organizations', orgId, 'fiscalPeriods', periodId));
  if (!period.exists() || period.data()?.status === 'closed') {
    throw new Error('Fiscal period is closed');
  }
}

export async function assertEntryDateInPeriod(
  db: Firestore,
  orgId: string,
  periodId: string,
  entryDate: string
): Promise<void> {
  const period = await getDoc(doc(db, 'organizations', orgId, 'fiscalPeriods', periodId));
  if (!period.exists()) throw new Error('Fiscal period not found');
  const startDate = String(period.data()?.startDate ?? '');
  const endDate = String(period.data()?.endDate ?? '');
  if (startDate && entryDate < startDate) {
    throw new Error('Entry date is before fiscal period start');
  }
  if (endDate && entryDate > endDate) {
    throw new Error('Entry date is after fiscal period end');
  }
}

export function assertOnOrAfterOpening(settings: CashbookSettings, entryDate: string): void {
  if (!settings.openingDate) return;
  if (entryDate < settings.openingDate) {
    throw new Error('Entry date is before cashbook opening date');
  }
}

/** Reject calendar dates after local today (YYYY-MM-DD). */
export function assertNotFutureDate(entryDate: string, today: string = localTodayIso()): void {
  if (entryDate > today) {
    throw new Error('Entry date cannot be in the future');
  }
}

export async function loadCurrentPeriodId(db: Firestore, orgId: string): Promise<string | null> {
  const main = await getDoc(doc(db, 'organizations', orgId, 'settings', 'main'));
  const periodId = String(main.data()?.currentPeriodId ?? '').trim();
  return periodId || null;
}

async function hasActiveCashbookEntries(db: Firestore, orgId: string): Promise<boolean> {
  const snap = await getDocs(
    query(cashbookEntriesCol(db, orgId), where('status', '==', 'active'))
  );
  return !snap.empty;
}

async function hasActiveDebtEntries(db: Firestore, orgId: string): Promise<boolean> {
  const snap = await getDocs(
    query(cashbookDebtEntriesCol(db, orgId), where('status', '==', 'active'))
  );
  return !snap.empty;
}

export async function hasActiveCashbookOrDebtEntries(db: Firestore, orgId: string): Promise<boolean> {
  const [cash, debt] = await Promise.all([
    hasActiveCashbookEntries(db, orgId),
    hasActiveDebtEntries(db, orgId)
  ]);
  return cash || debt;
}

export async function loadCashbookEntries(
  db: Firestore,
  orgId: string,
  endDate: string,
  minDate?: string
): Promise<CashbookEntryRecord[]> {
  try {
    const constraints: QueryConstraint[] = [];
    if (minDate) constraints.push(where('date', '>=', minDate));
    if (endDate) constraints.push(where('date', '<=', endDate));
    const snap = await getDocs(
      constraints.length
        ? query(cashbookEntriesCol(db, orgId), ...constraints)
        : cashbookEntriesCol(db, orgId)
    );
    return snap.docs.map((d) => mapCashbookEntry(d.id, d.data()));
  } catch (e) {
    if (!isFirestoreIndexError(e)) throw e;
    const snap = await getDocs(cashbookEntriesCol(db, orgId));
    return snap.docs
      .filter((d) => {
        const date = String(d.data().date ?? '');
        if (minDate && date < minDate) return false;
        if (date > endDate) return false;
        return true;
      })
      .map((d) => mapCashbookEntry(d.id, d.data()));
  }
}

export async function loadDebtEntries(
  db: Firestore,
  orgId: string,
  endDate: string,
  minDate?: string
): Promise<CashbookDebtEntryRecord[]> {
  try {
    const constraints: QueryConstraint[] = [];
    if (minDate) constraints.push(where('date', '>=', minDate));
    if (endDate) constraints.push(where('date', '<=', endDate));
    const snap = await getDocs(
      constraints.length
        ? query(cashbookDebtEntriesCol(db, orgId), ...constraints)
        : cashbookDebtEntriesCol(db, orgId)
    );
    return snap.docs.map((d) => mapDebtEntry(d.id, d.data()));
  } catch (e) {
    if (!isFirestoreIndexError(e)) throw e;
    const snap = await getDocs(cashbookDebtEntriesCol(db, orgId));
    return snap.docs
      .filter((d) => {
        const date = String(d.data().date ?? '');
        if (minDate && date < minDate) return false;
        if (date > endDate) return false;
        return true;
      })
      .map((d) => mapDebtEntry(d.id, d.data()));
  }
}

export async function loadActiveCashbookEntryIds(
  db: Firestore,
  orgId: string,
  startDate?: string,
  endDate?: string
): Promise<string[]> {
  try {
    const constraints: QueryConstraint[] = [where('status', '==', 'active')];
    if (startDate) constraints.push(where('date', '>=', startDate));
    if (endDate) constraints.push(where('date', '<=', endDate));
    const snap = await getDocs(query(cashbookEntriesCol(db, orgId), ...constraints));
    return snap.docs.map((d) => d.id);
  } catch (e) {
    if (!isFirestoreIndexError(e)) throw e;
    const snap = await getDocs(
      query(cashbookEntriesCol(db, orgId), where('status', '==', 'active'))
    );
    return snap.docs
      .filter((d) => {
        const date = String(d.data().date ?? '');
        if (startDate && date < startDate) return false;
        if (endDate && date > endDate) return false;
        return true;
      })
      .map((d) => d.id);
  }
}

export async function loadActiveDebtEntryIds(
  db: Firestore,
  orgId: string,
  startDate?: string,
  endDate?: string
): Promise<string[]> {
  try {
    const constraints: QueryConstraint[] = [where('status', '==', 'active')];
    if (startDate) constraints.push(where('date', '>=', startDate));
    if (endDate) constraints.push(where('date', '<=', endDate));
    const snap = await getDocs(query(cashbookDebtEntriesCol(db, orgId), ...constraints));
    return snap.docs.map((d) => d.id);
  } catch (e) {
    if (!isFirestoreIndexError(e)) throw e;
    const snap = await getDocs(
      query(cashbookDebtEntriesCol(db, orgId), where('status', '==', 'active'))
    );
    return snap.docs
      .filter((d) => {
        const date = String(d.data().date ?? '');
        if (startDate && date < startDate) return false;
        if (endDate && date > endDate) return false;
        return true;
      })
      .map((d) => d.id);
  }
}

export async function loadActiveEntriesForDuplicateCheck(
  db: Firestore,
  orgId: string
): Promise<ImportDuplicateKey[]> {
  const snap = await getDocs(
    query(cashbookEntriesCol(db, orgId), where('status', '==', 'active'))
  );
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      date: String(data.date ?? ''),
      type: data.type === 'in' ? 'in' : 'out',
      amount: roundMoney(String(data.amount ?? '0')),
      categoryKey: String(data.categoryKey ?? '')
    } as ImportDuplicateKey;
  });
}

export async function loadAllDebtEntries(
  db: Firestore,
  orgId: string
): Promise<CashbookDebtEntryRecord[]> {
  const settings = await loadCashbookSettings(db, orgId);
  const minDate = isCashbookOpeningConfigured(settings) ? settings.openingDate : undefined;
  return loadDebtEntries(db, orgId, '9999-12-31', minDate);
}

export async function loadAllDebtOwners(db: Firestore, orgId: string): Promise<string[]> {
  const settings = await loadCashbookSettings(db, orgId);
  const entries = await loadDebtEntries(
    db,
    orgId,
    '9999-12-31',
    isCashbookOpeningConfigured(settings) ? settings.openingDate : undefined
  );
  return collectDebtOwners(entries, settings);
}

export async function loadCashbookContext(
  db: Firestore,
  orgId: string,
  endDate: string
): Promise<CashbookContext> {
  let settings = await loadCashbookSettings(db, orgId);
  settings = await ensureOpeningDebtLinesMigrated(db, orgId, settings);
  const minDate = isCashbookOpeningConfigured(settings) ? settings.openingDate : undefined;
  const loadThrough = endDate > localTodayIso() ? endDate : localTodayIso();
  const cacheKey = `${orgId}:${minDate ?? '*'}:${loadThrough}`;
  const epochAtStart = cashbookContextEpoch(orgId);
  const cached = cashbookContextCache.get(cacheKey);
  if (
    cached &&
    cached.epoch === epochAtStart &&
    cached.expiresAt > Date.now()
  ) {
    return cached.value;
  }

  const [entries, debtEntries, hasActiveEntries] = await Promise.all([
    loadCashbookEntries(db, orgId, loadThrough, minDate),
    loadDebtEntries(db, orgId, loadThrough, minDate),
    hasActiveCashbookOrDebtEntries(db, orgId)
  ]);
  const movements = buildCashbookMovements(entries);
  const debtMovements = buildDebtMovements(debtEntries);
  const ctx: CashbookContext = {
    settings,
    entries,
    debtEntries,
    movements,
    debtMovements,
    hasActiveEntries
  };
  // Only populate cache if nothing invalidated us while we were fetching.
  if (shouldAcceptCashbookCacheWrite(orgId, epochAtStart)) {
    cashbookContextCache.set(cacheKey, {
      expiresAt: Date.now() + CASHBOOK_CONTEXT_TTL_MS,
      epoch: epochAtStart,
      value: ctx
    });
  }
  return ctx;
}
